import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import API_BASE_URL from "./config";
import { FolderCard } from "./components/FolderManager";
import "./UploadLinkPage.css";

/**
 * Public co-worker upload page: /upload/:token
 *
 * Works whether or not a studio is logged in on this browser. It never reads or
 * writes the studio's own session keys — the link session lives under its own
 * localStorage key so a refresh doesn't re-prompt for the PIN, and every upload
 * started here carries that session (see UploadManager's per-upload auth).
 */

const sessionKey = (token) => `upload_link_session_${token}`;

function readSession(token) {
  try {
    const raw = localStorage.getItem(sessionKey(token));
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s?.session_token || !s.expires_at || Number(s.expires_at) <= Date.now()) {
      localStorage.removeItem(sessionKey(token));
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

function UploadLinkPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);      // { event_name, studio_name, status }
  const [phase, setPhase] = useState("loading"); // loading | invalid | closed | paused | pin | upload
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [session, setSession] = useState(null); // verify response
  const [pausedMessage, setPausedMessage] = useState(""); // 402: studio subscription expired
  const [folders, setFolders] = useState([]);
  const [generalCount, setGeneralCount] = useState(null);

  // Folder list + the General ("unsorted") count. Both endpoints are public
  // reads keyed by event id, the same ones the studio's own page uses.
  const refreshFolders = useCallback(async (eventId) => {
    try {
      const [fRes, eRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/events/${eventId}/folders`),
        fetch(`${API_BASE_URL}/api/events/single/${eventId}`),
      ]);
      const fData = fRes.ok ? await fRes.json() : null;
      const eData = eRes.ok ? await eRes.json() : null;
      const list = fData?.folders || [];
      if (fData) setFolders(list);
      if (eData?.event && typeof eData.event.image_count === "number") {
        const inFolders = list.reduce((n, f) => n + (f.image_count || 0), 0);
        setGeneralCount(Math.max(0, eData.event.image_count - inFolders));
      }
    } catch { /* keep whatever we have */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/upload-link/${token}`);
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.status === 404) { setPhase("invalid"); return; }
        if (!res.ok) throw new Error(data.message || "Could not open this link");
        setInfo(data);
        if (data.status !== "active") { setPhase("closed"); return; }
        const saved = readSession(token);
        if (saved) {
          setSession(saved);
          setFolders(saved.folders || []);
          setPhase("upload");
          refreshFolders(saved.event.id);
        } else {
          setPhase("pin");
        }
      } catch (err) {
        if (!cancelled) { setInfo({ error: err.message }); setPhase("invalid"); }
      }
    })();
    return () => { cancelled = true; };
  }, [token, refreshFolders]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!pin) return;
    setVerifying(true);
    setPinError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/upload-link/${token}/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 410) { setInfo((i) => ({ ...i, status: data.status || "expired" })); setPhase("closed"); return; }
      if (res.status === 402) { setPausedMessage(data.message || "Uploads are paused for this studio."); setPhase("paused"); return; }
      if (res.status === 429) { setPinError("Too many attempts. Please wait a few minutes and try again."); return; }
      if (!res.ok) { setPinError(data.message || "Incorrect PIN"); return; }
      try { localStorage.setItem(sessionKey(token), JSON.stringify(data)); } catch { /* private mode */ }
      setSession(data);
      setFolders(data.folders || []);
      setPin("");
      setPhase("upload");
      refreshFolders(data.event.id);
    } catch {
      setPinError("Network error. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const signOut = () => {
    try { localStorage.removeItem(sessionKey(token)); } catch { /* ignore */ }
    setSession(null);
    setPhase("pin");
  };

  const eventName = session?.event?.event_name || info?.event_name || "Event";

  if (phase === "loading") {
    return (
      <div className="ul-page ul-center">
        <div className="ul-spinner" />
        <p>Opening upload link…</p>
      </div>
    );
  }

  if (phase === "invalid") {
    return (
      <div className="ul-page ul-center">
        <div className="ul-card ul-card-narrow">
          <h1>Link not found</h1>
          <p>{info?.error || "This upload link doesn’t exist. Check the address with the studio that shared it."}</p>
        </div>
      </div>
    );
  }

  if (phase === "paused") {
    return (
      <div className="ul-page ul-center">
        <div className="ul-card ul-card-narrow">
          <h1>Uploads paused</h1>
          <p>{pausedMessage}</p>
          <p>Ask {info?.studio_name || "the studio"} to renew, then try the link again.</p>
        </div>
      </div>
    );
  }

  if (phase === "closed") {
    const revoked = info?.status === "revoked";
    return (
      <div className="ul-page ul-center">
        <div className="ul-card ul-card-narrow">
          <h1>{revoked ? "Link revoked" : "Link expired"}</h1>
          <p>
            {revoked
              ? "The studio has turned this upload link off."
              : "This upload link is no longer active."}{" "}
            Ask {info?.studio_name || "the studio"} for a new one.
          </p>
        </div>
      </div>
    );
  }

  if (phase === "pin") {
    return (
      <div className="ul-page ul-center">
        <form className="ul-card ul-card-narrow" onSubmit={handleVerify}>
          <span className="ul-kicker">{info?.studio_name || "Photentra"}</span>
          <h1>{info?.event_name}</h1>
          <p>Enter the PIN the studio gave you to start uploading photos.</p>
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={8}
            autoComplete="one-time-code"
            className="ul-pin-input"
            placeholder="PIN"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            aria-label="PIN"
            autoFocus
          />
          {pinError && <p className="ul-error" role="alert">{pinError}</p>}
          <button type="submit" className="ul-btn-primary" disabled={verifying || pin.length < 4}>
            {verifying ? "Checking…" : "Continue"}
          </button>
        </form>
      </div>
    );
  }

  const event = session.event;

  return (
    <div className="ul-page">
      <header className="ul-header">
        <div>
          <span className="ul-kicker">{info?.studio_name || "Photentra"} · Uploading as co-worker</span>
          <h1>{eventName}</h1>
        </div>
        <button type="button" className="ul-btn-secondary" onClick={signOut}>Sign out</button>
      </header>

      <main className="ul-main">
        <p className="ul-help">
          Drag photos onto a folder, or click a folder to choose files. Uploads continue in the background — keep this tab open until they finish.
        </p>
        <div className="fm-section">
          <div className="fm-grid">
            <FolderCard
              eventId={event.id}
              eventName={eventName}
              folderId={null}
              name={event.general_folder_name || "General"}
              imageCount={generalCount}
              deletable={false}
              renamable={false}
              isProMax={!!session.is_pro_max}
              authToken={session.session_token}
            />
            {folders.map((f) => (
              <FolderCard
                key={f.id}
                eventId={event.id}
                eventName={eventName}
                folderId={f.id}
                name={f.name}
                imageCount={f.image_count}
                deletable={false}
                renamable={false}
                isProMax={!!session.is_pro_max}
                authToken={session.session_token}
              />
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

export default UploadLinkPage;
